import type { EntityProfileLookupOption as PatientProfileLookupOption } from "../../../../entityProfileModal/components/EntityProfileLookupSelect";
import { phoneFormatter } from "../../../../../shared/utils/phone-formatter";
import type { PatientProfilePatient } from "../../../patientProfileModal.types";
import { formatPatientProfileValue } from "../../../patientProfileModal.utils";
import type {
  FamilyInformationField,
  FamilyInformationMember,
  PatientInformationDraft,
  PatientInformationField,
} from "./patientInformation.types";

export const hhaGenderOptions = [
  { value: "F", label: "Female" },
  { value: "M", label: "Male" },
] satisfies Array<{
  value: NonNullable<PatientInformationDraft["preferredHHAGender"]>;
  label: string;
}>;

export const yesNoOptions = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

export const kidsInHouseOptions = [0, 1, 2, 3, 4, 5, 6].map((value) => ({
  value,
  label: String(value),
}));

export function getAgencyMemberLookupOption(
  id: number | null | undefined
): PatientProfileLookupOption | null {
  return id == null ? null : { id, displayName: `Agency member #${id}` };
}

export function getAgencyMemberDisplayValue(id: number | null | undefined) {
  return id == null ? "" : `Agency member #${id}`;
}

export function getSocialWorkerLookupOption(
  patient: PatientProfilePatient
): PatientProfileLookupOption | null {
  if (patient.socialWorker === null) {
    return null;
  }

  return {
    id: patient.socialWorker.id,
    displayName: `${patient.socialWorker.firstName} ${patient.socialWorker.lastName}`,
    description: patient.socialWorker.email,
  };
}

export function getSocialWorkerDisplayValue(
  patient: PatientProfilePatient,
  id: number | null | undefined
) {
  if (id == null) {
    return "";
  }

  if (patient.socialWorker?.id === id) {
    return `${patient.socialWorker.firstName} ${patient.socialWorker.lastName}`;
  }

  return `Social worker #${id}`;
}

export function getSourceLookupOption(
  patient: PatientProfilePatient
): PatientProfileLookupOption | null {
  if (patient.source === null) {
    return null;
  }

  return {
    id: patient.source.id,
    displayName: patient.source.displayName,
    description: patient.source.type,
  };
}

export function getSourceDisplayValue(
  patient: PatientProfilePatient,
  id: number | null | undefined
) {
  if (id == null) {
    return "";
  }

  if (patient.source?.id === id) {
    return patient.source.displayName;
  }

  return `Source #${id}`;
}

export function isHhaGenderOption(
  value: PatientInformationDraft["preferredHHAGender"]
): value is NonNullable<PatientInformationDraft["preferredHHAGender"]> {
  return hhaGenderOptions.some((option) => option.value === value);
}

export function createInformationDraft(patient: PatientProfilePatient): PatientInformationDraft {
  return {
    email: patient.email,
    generalNotes: patient.generalNotes,
    assignedDPS: patient.assignedDPS,
    assignedIntake: patient.assignedIntake,
    assignedSalesRep: patient.assignedSalesRep,
    kidsInHouse: patient.kidsInHouse,
    kosherHome: patient.kosherHome,
    medicaidNumber: patient.medicaidNumber,
    medicareNumber: patient.medicareNumber,
    patientHIClaim: patient.patientHIClaim,
    pets: patient.pets,
    preferredHHAGender: patient.preferredHHAGender as PatientInformationDraft["preferredHHAGender"],
    smoking: patient.smoking,
    socialWorker: patient.socialWorker?.id ?? null,
    source: patient.source?.id ?? null,
    ssn: patient.ssn,
    wageParityNumber: patient.wageParityNumber,
  };
}

export function createFamilyInformationDraft(
  patient: PatientProfilePatient
): FamilyInformationMember[] {
  return (patient.familyInformation ?? []).map((member) => ({ ...member }));
}

export function createEmptyFamilyMember(): FamilyInformationMember {
  return {
    firstName: "",
    homePhoneNumber: "",
    lastName: "",
    mobilePhoneNumber: "",
    relationship: "",
  };
}

export function nullableText(value: string) {
  return value.trim() === "" ? null : value;
}

export function nullableFamilyText(value: string) {
  return value.trim();
}

export function formatFamilyPhoneNumber(value: string | null | undefined) {
  return value == null || value === "" ? "" : phoneFormatter.formatNationalIfValid(value);
}

export function normalizeFamilyInformation(
  familyDraft: FamilyInformationMember[]
): FamilyInformationMember[] | null {
  const normalizedFamilyDraft = familyDraft.map((member) => ({ ...member }));

  for (const member of normalizedFamilyDraft) {
    for (const field of [
      "mobilePhoneNumber",
      "homePhoneNumber",
    ] satisfies FamilyInformationField[]) {
      const value = member[field];

      if (value === "") {
        continue;
      }

      if (!phoneFormatter.isValidNumber(value)) {
        return null;
      }

      member[field] = phoneFormatter.formatE164(value);
    }
  }

  return normalizedFamilyDraft;
}

export function getDraftDisplayValue(
  patient: PatientProfilePatient,
  draft: PatientInformationDraft | null,
  field: PatientInformationField
) {
  const currentValue = draft?.[field] ?? createInformationDraft(patient)[field];

  if (field === "source") {
    return getSourceDisplayValue(patient, currentValue as number | null);
  }

  if (field === "socialWorker") {
    return getSocialWorkerDisplayValue(patient, currentValue as number | null);
  }

  if (field === "assignedDPS" || field === "assignedIntake" || field === "assignedSalesRep") {
    return getAgencyMemberDisplayValue(currentValue as number | null);
  }

  if (field === "preferredHHAGender") {
    return hhaGenderOptions.find((option) => option.value === currentValue)?.label ?? "";
  }

  return formatPatientProfileValue(currentValue);
}
